// FRONT-2195: re-attach cadence for a chat turn whose PREVIOUS reconnect
// pursuit settled cleanly (the tail ended without an error — a resume-
// endpoint ceiling close, a benign proxy drop, or any other non-error end)
// while the server still reports the turn as live. `resume-retry-policy.ts`
// covers retries WITHIN one pursuit (a failed reconnect request); this file
// covers STARTING A NEW pursuit once one has already ended.
//
// Two pure decisions, kept separate from use-agent-chat.ts so both are
// unit-testable without mounting the hook:
//
//   - `isEligibleForReattach` — should a new pursuit even be considered
//     right now? All the non-timing conditions (the exact contract the
//     ticket specifies): the fresh history still names this streamId as the
//     trailing row with `streamLive === true`, nothing is fetching, the
//     local SDK isn't already streaming/submitted, this streamId isn't
//     permanently suppressed (a deliberate Stop — the ONLY stop signal, and
//     a per-turn one), and no pursuit already owns it (in flight or backing
//     off).
//   - `nextReattachCadence` — given how long the pursuit that just settled
//     stayed attached, how long to wait before the NEXT one, and the
//     updated streak used to compute the delay after that. A previous
//     pursuit that stayed attached a healthy amount of time (>= 30s) means
//     the connection is stable — wait only ~1s and reset the streak. A
//     previous pursuit that closed quickly means something is flaky —
//     escalate 2s -> 6s -> 15s (capped) so a resume that keeps closing
//     instantly can never hot-loop.
//
// The caller (resume-pursuit-controller.ts) only schedules a timer for the
// returned delay and, when it fires, REFETCHES /history and re-checks
// `isEligibleForReattach` against that fresh answer (state can change during
// the wait) — it never re-derives this logic itself.

export interface ReattachEligibilityInput {
  /**
   * `/history`'s `streamLive` flag for the trailing row that still names
   * this streamId, resolved against the FRESHEST snapshot available at
   * decision time — `false` for a final turn, an orphaned one, or one whose
   * trailing row no longer names this streamId at all (a different turn
   * started). A turn that is final or orphaned must never re-attach, which
   * this single field is enough to guarantee.
   */
  streamLive: boolean;
  /** The history query is mid-refetch — never start a pursuit against a
   *  snapshot that might be replaced a moment later. */
  isFetching: boolean;
  /** The local AI SDK chat status. Streaming/submitted already owns the
   *  screen — never re-attach on top of a live local turn. */
  localStatus: string;
  /** This streamId was poisoned by a deliberate Stop (FRONT-765) — permanent
   *  for this chat instance, never re-attach it. Deliberately the only stop
   *  input (FRONT-2195 review F5): the hook's instance-wide `manuallyStopped`
   *  flag outlives the turn it was set for, so gating on it blocked every
   *  re-arm of the NEXT turn. Stop suppresses the turn it stopped, by id. */
  suppressed: boolean;
  /** A pursuit for this streamId already owns the outcome (in flight,
   *  backing off, or — for a non-live turn — permanently given up). */
  pursuitOwned: boolean;
}

/**
 * Pure eligibility check for starting a NEW pursuit after a previous one for
 * the same streamId has settled. Every branch is independently provable:
 * flip any one input away from its "eligible" value and this must return
 * false on that input alone.
 */
export function isEligibleForReattach(input: ReattachEligibilityInput): boolean {
  if (!input.streamLive) return false;
  if (input.isFetching) return false;
  if (input.localStatus === 'streaming' || input.localStatus === 'submitted') return false;
  if (input.suppressed) return false;
  if (input.pursuitOwned) return false;
  return true;
}

/** A previous pursuit that stayed attached at least this long is "stable" —
 *  the next one waits only the short stable delay, and the escalation
 *  streak resets to 0. */
export const STABLE_ATTACH_MS = 30_000;

/** Delay before the next pursuit when the previous one was stable. */
const STABLE_REATTACH_DELAY_MS = 1_000;

/** Escalating delays for consecutive pursuits that each closed before
 *  STABLE_ATTACH_MS, indexed by (streak - 1) and capped at the last entry —
 *  2s, 6s, then 15s forever until a pursuit finally stays attached. */
const ESCALATING_REATTACH_DELAYS_MS: readonly number[] = [2_000, 6_000, 15_000];

export interface ReattachCadenceState {
  /** Consecutive settled pursuits for this streamId that each attached for
   *  less than STABLE_ATTACH_MS, since the last stable one (or since the
   *  very first re-arm). Resets to 0 the moment a pursuit is stable. */
  quickSettleStreak: number;
}

export interface ReattachCadenceResult {
  /** Updated cadence state to store for this streamId's next settle. */
  state: ReattachCadenceState;
  /** Milliseconds to wait before the next pursuit may start. */
  delayMs: number;
}

/**
 * Given the cadence state carried from this streamId's prior settles and
 * how long the pursuit that JUST settled stayed attached, compute the delay
 * before the next pursuit and the updated streak.
 */
export function nextReattachCadence(
  state: ReattachCadenceState,
  attachedMs: number,
): ReattachCadenceResult {
  if (attachedMs >= STABLE_ATTACH_MS) {
    return { state: { quickSettleStreak: 0 }, delayMs: STABLE_REATTACH_DELAY_MS };
  }
  const quickSettleStreak = state.quickSettleStreak + 1;
  const idx = Math.min(quickSettleStreak - 1, ESCALATING_REATTACH_DELAYS_MS.length - 1);
  return { state: { quickSettleStreak }, delayMs: ESCALATING_REATTACH_DELAYS_MS[idx] };
}
